import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Plus, X, FileCode, FolderOpen, BookOpen, Pencil } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";

interface TodoCategory {
  id: string;
  title: string;
  description: string;
  scopeFiles?: string; // JSON string
  refs?: string; // JSON string
  order: number;
}

interface ScopeFiles {
  frontend?: string[];
  backend?: string[];
  shared?: string[];
  tests?: string[];
}

interface Reference {
  title: string;
  url: string;
}

interface CategoryEditModalProps {
  category: TodoCategory | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (updates: Partial<TodoCategory>) => Promise<void>;
}

export function CategoryEditModal({ category, isOpen, onClose, onSave }: CategoryEditModalProps) {
  const [formData, setFormData] = useState({
    title: "",
    description: "",
  });

  const [scopeFiles, setScopeFiles] = useState<ScopeFiles>({
    frontend: [],
    backend: [],
    shared: [],
    tests: [],
  });

  const [refs, setRefs] = useState<Reference[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  // New file path inputs
  const [newPaths, setNewPaths] = useState({
    frontend: "",
    backend: "",
    shared: "",
    tests: "",
  });

  // New reference inputs
  const [newRef, setNewRef] = useState({ title: "", url: "" });

  // Reset form when category changes
  useEffect(() => {
    if (category) {
      // Editing existing category
      setFormData({
        title: category.title,
        description: category.description,
      });

      // Parse scopeFiles
      try {
        const parsed = category.scopeFiles ? JSON.parse(category.scopeFiles) : {};
        setScopeFiles({
          frontend: parsed.frontend || [],
          backend: parsed.backend || [],
          shared: parsed.shared || [],
          tests: parsed.tests || [],
        });
      } catch {
        setScopeFiles({ frontend: [], backend: [], shared: [], tests: [] });
      }

      // Parse refs
      try {
        const parsed = category.refs ? JSON.parse(category.refs) : [];
        setRefs(Array.isArray(parsed) ? parsed : []);
      } catch {
        setRefs([]);
      }
    } else {
      // Creating new category - reset to empty
      setFormData({
        title: "",
        description: "",
      });
      setScopeFiles({ frontend: [], backend: [], shared: [], tests: [] });
      setRefs([]);
    }
  }, [category, isOpen]);

  const addPath = (type: keyof ScopeFiles) => {
    const path = newPaths[type].trim();
    if (path) {
      setScopeFiles({
        ...scopeFiles,
        [type]: [...(scopeFiles[type] || []), path],
      });
      setNewPaths({ ...newPaths, [type]: "" });
    }
  };

  const removePath = (type: keyof ScopeFiles, index: number) => {
    setScopeFiles({
      ...scopeFiles,
      [type]: (scopeFiles[type] || []).filter((_, i) => i !== index),
    });
  };

  const addReference = () => {
    if (newRef.title.trim() && newRef.url.trim()) {
      setRefs([...refs, { title: newRef.title.trim(), url: newRef.url.trim() }]);
      setNewRef({ title: "", url: "" });
    }
  };

  const removeReference = (index: number) => {
    setRefs(refs.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave({
        title: formData.title,
        description: formData.description,
        scopeFiles: JSON.stringify(scopeFiles),
        refs: JSON.stringify(refs),
      });
      onClose();
    } catch (error) {
      console.error("Failed to save category:", error);
    } finally {
      setIsSaving(false);
    }
  };


  const ScopeSection = ({ type, icon: Icon }: { type: keyof ScopeFiles; icon: React.ElementType }) => {
    const label = type.charAt(0).toUpperCase() + type.slice(1);

    return (
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-muted-foreground" />
          <Label className="text-sm font-medium">{label}</Label>
        </div>

        <div className="space-y-2">
          {(scopeFiles[type] || []).map((path, index) => (
            <div
              key={index}
              className="flex items-center gap-2 p-2 bg-muted/50 rounded-md group hover:bg-muted transition-colors"
            >
              <code className="flex-1 text-xs font-mono text-foreground">{path}</code>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => removePath(type, index)}
                className="h-7 w-7 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <X className="w-3 h-3" />
              </Button>
            </div>
          ))}

          <div className="flex gap-2">
            <Input
              placeholder={`e.g., apps/web-app/src/${type}/**`}
              value={newPaths[type]}
              onChange={(e) => setNewPaths({ ...newPaths, [type]: e.target.value })}
              onKeyDown={(e) => e.key === "Enter" && addPath(type)}
              className="font-mono text-xs h-9"
            />
            <Button
              onClick={() => addPath(type)}
              variant="outline"
              size="sm"
              className="h-9 w-9 p-0"
            >
              <Plus className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-[90vw] h-[90vh] max-w-none flex flex-col p-0">
        {/* Header */}
        <DialogHeader className="border-b px-6 py-4 bg-muted/30">
          <DialogTitle className="text-xl">{category ? "Edit Category" : "Create Category"}</DialogTitle>
          <div className="mt-3">
            <Input
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="text-lg font-semibold"
              placeholder="Category title..."
            />
          </div>
        </DialogHeader>

        {/* Tabbed Content */}
        <div className="flex-1 overflow-hidden">
          <Tabs defaultValue="description" className="h-full flex flex-col">
            <TabsList className="mx-6 mt-4 grid w-full max-w-[500px] grid-cols-3">
              <TabsTrigger value="description" className="gap-2">
                <Pencil className="w-4 h-4" />
                Description
              </TabsTrigger>
              <TabsTrigger value="scope" className="gap-2">
                <FolderOpen className="w-4 h-4" />
                Scope Files
              </TabsTrigger>
              <TabsTrigger value="refs" className="gap-2">
                <BookOpen className="w-4 h-4" />
                References
              </TabsTrigger>
            </TabsList>

            {/* Description Tab */}
            <TabsContent value="description" className="flex-1 mt-4 overflow-hidden">
              <ScrollArea className="h-full px-6 pb-6">
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="min-h-[500px] text-sm font-mono resize-none"
                  placeholder="Category description in markdown..."
                />
              </ScrollArea>
            </TabsContent>

            {/* Scope Files Tab */}
            <TabsContent value="scope" className="flex-1 mt-4 overflow-hidden">
              <ScrollArea className="h-full px-6 pb-6">
                <div className="grid gap-6">
                  <ScopeSection type="frontend" icon={FileCode} />
                  <ScopeSection type="backend" icon={FileCode} />
                  <ScopeSection type="shared" icon={FileCode} />
                  <ScopeSection type="tests" icon={FileCode} />
                </div>
              </ScrollArea>
            </TabsContent>

            {/* References Tab */}
            <TabsContent value="refs" className="flex-1 mt-4 overflow-hidden">
              <ScrollArea className="h-full px-6 pb-6">
                <div className="space-y-4">
                  {refs.map((ref, index) => (
                    <div
                      key={index}
                      className="flex items-start gap-3 p-4 border rounded-lg group hover:border-primary/50 hover:bg-accent/50 transition-all"
                    >
                      <BookOpen className="w-5 h-5 mt-0.5 text-primary" />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm mb-1">{ref.title}</div>
                        <code className="text-xs text-muted-foreground break-all">{ref.url}</code>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeReference(index)}
                        className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}

                  {/* Add new reference */}
                  <div className="border-2 border-dashed rounded-lg p-4 space-y-3">
                    <div>
                      <Label className="text-xs mb-1.5">Title</Label>
                      <Input
                        placeholder="e.g., Canvas Architecture"
                        value={newRef.title}
                        onChange={(e) => setNewRef({ ...newRef, title: e.target.value })}
                        className="h-9"
                      />
                    </div>
                    <div>
                      <Label className="text-xs mb-1.5">URL</Label>
                      <div className="flex gap-2">
                        <Input
                          placeholder="e.g., docs/technical/architecture/CANVAS.md"
                          value={newRef.url}
                          onChange={(e) => setNewRef({ ...newRef, url: e.target.value })}
                          onKeyDown={(e) => e.key === "Enter" && addReference()}
                          className="font-mono text-xs h-9"
                        />
                        <Button
                          onClick={addReference}
                          variant="outline"
                          size="sm"
                          className="h-9 w-9 p-0"
                        >
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </ScrollArea>
            </TabsContent>
          </Tabs>
        </div>

        {/* Footer */}
        <DialogFooter className="border-t px-6 py-4 bg-muted/20">
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving ? "Saving..." : "Save Changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
